import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const WhatsAppIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const FollowUp = () => {
  const [members, setMembers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [memberFollowUps, setMemberFollowUps] = useState([]);
  const [leadFollowUps, setLeadFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');
  const [actionFilter, setActionFilter] = useState('all');
  const [leadFilter, setLeadFilter] = useState('all');
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemType, setItemType] = useState('member'); // 'member' or 'lead'
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [error, setError] = useState('');
  const [messageSettings, setMessageSettings] = useState({
    whatsappTemplate: 'Hi {name}, your gym membership is {status}. Please renew to continue your fitness journey!',
    emailTemplate: 'Dear {name},\n\nThis is a reminder that your gym membership {status}.\n\nPlease renew at your earliest convenience.\n\nThank you,\n{gymName}',
    emailSubject: 'Gym Membership Renewal Reminder - {gymName}'
  });

  const [followUpForm, setFollowUpForm] = useState({
    notes: '',
    response: '',
    nextFollowUpDate: '',
    status: '',
    contactMode: 'WhatsApp',
    outcome: 'Promised'
  });

  useEffect(() => {
    fetchAllData();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.settings) {
        setMessageSettings(prev => ({
          ...prev,
          ...res.data.settings
        }));
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [membersRes, leadsRes, followUpsRes] = await Promise.all([
        axios.get('/api/members', { headers }),
        axios.get('/api/leads', { headers }),
        axios.get('/api/followups', { headers })
      ]);

      setMembers(membersRes.data.members || []);
      setLeads(leadsRes.data.leads || []);
      setMemberFollowUps(followUpsRes.data.followUps || []);
      
      // Fetch lead follow-ups for each lead
      const leadIds = (leadsRes.data.leads || []).map(l => l.id);
      if (leadIds.length > 0) {
        const leadFollowUpsPromises = leadIds.map(id =>
          axios.get(`/api/leads/${id}/followups`, { headers }).catch(() => ({ data: { followups: [] } }))
        );
        const leadFollowUpsResults = await Promise.all(leadFollowUpsPromises);
        const allLeadFollowUps = leadFollowUpsResults.flatMap((r, idx) =>
          (r.data.followups || r.data.followUps || []).map(f => ({ ...f, leadId: leadIds[idx] }))
        );
        setLeadFollowUps(allLeadFollowUps);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Member action items (overdue, upcoming renewals, trial conversions)
  const todoItems = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const items = [];
    
    members.forEach(m => {
      if (m.renewalDue) {
        const dueDate = new Date(m.renewalDue);
        dueDate.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
        
        if (diffDays >= 0 && diffDays <= 7) {
          items.push({
            type: 'renewal',
            priority: diffDays <= 3 ? 'high' : 'medium',
            member: m,
            message: `${m.name} - Renewal due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`,
            action: 'Contact for renewal',
            daysUntil: diffDays
          });
        } else if (diffDays < 0 && diffDays >= -14) {
          items.push({
            type: 'overdue',
            priority: 'high',
            member: m,
            message: `${m.name} - Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`,
            action: 'Follow up immediately',
            daysOverdue: Math.abs(diffDays)
          });
        }
      }
      
      if (m.tenure === 1) {
        const joinDate = new Date(m.joiningDate);
        const daysSinceJoin = Math.ceil((today - joinDate) / (1000 * 60 * 60 * 24));
        if (daysSinceJoin >= 20 && daysSinceJoin <= 30) {
          items.push({
            type: 'trial',
            priority: 'medium',
            member: m,
            message: `${m.name} - Trial member (${daysSinceJoin} days)`,
            action: 'Convert to regular membership',
            daysSinceJoin
          });
        }
      }
    });
    
    return items.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
  }, [members]);

  // Lead follow-up items
  const leadItems = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const items = [];
    
    leads.filter(l => l.status !== 'Converted').forEach(lead => {
      if (lead.nextFollowUpDate) {
        const followUpDate = new Date(lead.nextFollowUpDate);
        followUpDate.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((followUpDate - today) / (1000 * 60 * 60 * 24));
        
        if (diffDays < 0) {
          items.push({
            type: 'overdue',
            priority: 'high',
            lead,
            message: `${lead.name} - Follow-up overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`,
            action: 'Contact immediately',
            daysOverdue: Math.abs(diffDays)
          });
        } else if (diffDays === 0) {
          items.push({
            type: 'today',
            priority: 'high',
            lead,
            message: `${lead.name} - Follow-up due today`,
            action: 'Contact today',
            daysUntil: 0
          });
        } else if (diffDays <= 3) {
          items.push({
            type: 'upcoming',
            priority: 'medium',
            lead,
            message: `${lead.name} - Follow-up in ${diffDays} day${diffDays !== 1 ? 's' : ''}`,
            action: 'Prepare to contact',
            daysUntil: diffDays
          });
        }
      } else if (lead.status === 'New' || lead.status === 'Contacted') {
        items.push({
          type: 'pending',
          priority: lead.status === 'New' ? 'medium' : 'low',
          lead,
          message: `${lead.name} - No follow-up scheduled (${lead.status})`,
          action: 'Schedule follow-up'
        });
      }
    });
    
    return items.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
  }, [leads]);

  const filteredTodoItems = useMemo(() => {
    if (actionFilter === 'all') return todoItems;
    return todoItems.filter(item => item.type === actionFilter);
  }, [todoItems, actionFilter]);

  const filteredLeadItems = useMemo(() => {
    if (leadFilter === 'all') return leadItems;
    return leadItems.filter(item => item.type === leadFilter);
  }, [leadItems, leadFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const generateMessage = (template, item, statusText) => {
    if (!template) {
      template = 'Hi {name}, your gym membership is {status}. Please renew to continue your fitness journey!';
    }
    return template
      .replace(/{name}/g, item?.name || '')
      .replace(/{phone}/g, item?.phone || '')
      .replace(/{email}/g, item?.email || '')
      .replace(/{status}/g, statusText || '');
  };

  const handleLogFollowUp = (item, type) => {
    setSelectedItem(item);
    setItemType(type);
    setFollowUpForm({
      notes: '',
      response: '',
      nextFollowUpDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: type === 'lead' ? item.status : '',
      contactMode: 'WhatsApp',
      outcome: 'Promised'
    });
    setShowFollowUpModal(true);
  };

  const handleSubmitFollowUp = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (itemType === 'member') {
        await axios.post('/api/followups', {
          memberId: selectedItem.id,
          contactMode: followUpForm.contactMode,
          notes: followUpForm.notes,
          outcome: followUpForm.outcome
        }, { headers });
      } else {
        await axios.post(`/api/leads/${selectedItem.id}/followup`, {
          response: followUpForm.response,
          nextFollowUpDate: followUpForm.nextFollowUpDate || null,
          statusAfter: followUpForm.status || selectedItem.status
        }, { headers });
      }

      setShowFollowUpModal(false);
      fetchAllData();
    } catch (err) {
      console.error('Error logging follow-up:', err);
      setError('Failed to log follow-up');
    }
  };

  const toggleMember = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const toggleAllMembers = (items) => {
    const ids = items.map(i => i.member?.id || i.lead?.id);
    if (selectedMembers.length === ids.length) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(ids);
    }
  };

  const handleBulkWhatsApp = (items) => {
    const itemsToMessage = items.filter(i => selectedMembers.includes(i.member?.id || i.lead?.id));
    if (itemsToMessage.length === 0) {
      alert('Please select at least one item');
      return;
    }
    if (itemsToMessage.length > 10) {
      if (!window.confirm(`You are about to open ${itemsToMessage.length} WhatsApp tabs. Continue?`)) return;
    }
    itemsToMessage.forEach((item, index) => {
      const entity = item.member || item.lead;
      const statusText = item.type === 'overdue' ? 'expired' : item.type === 'renewal' ? 'expiring soon' : 'follow up needed';
      const message = generateMessage(messageSettings.whatsappTemplate, entity, statusText);
      const phone = entity.phone?.replace(/\D/g, '');
      const url = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
      setTimeout(() => window.open(url, '_blank'), index * 500);
    });
  };

  const filterButtonStyle = (isActive) => ({
    padding: '8px 16px',
    border: 'none',
    background: isActive ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f0f4f8',
    color: isActive ? 'white' : '#4a5568',
    cursor: 'pointer',
    fontSize: '13px',
    borderRadius: '20px',
    transition: 'all 0.2s',
    boxShadow: isActive ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none'
  });

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#888' }}>Loading follow-ups...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        📋 Follow-Up Center
      </h2>

      {error && (
        <div style={{ padding: '10px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '5px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button
          onClick={() => { setActiveTab('members'); setSelectedMembers([]); }}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'members' ? '#667eea' : '#f5f5f5',
            color: activeTab === 'members' ? '#fff' : '#333',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: activeTab === 'members' ? 'bold' : 'normal',
            fontSize: '14px'
          }}
        >
          👥 Member Follow-ups ({todoItems.length})
        </button>
        <button
          onClick={() => { setActiveTab('leads'); setSelectedMembers([]); }}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'leads' ? '#9C27B0' : '#f5f5f5',
            color: activeTab === 'leads' ? '#fff' : '#333',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: activeTab === 'leads' ? 'bold' : 'normal',
            fontSize: '14px'
          }}
        >
          🎯 Lead Follow-ups ({leadItems.length})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={{
            padding: '12px 24px',
            backgroundColor: activeTab === 'history' ? '#FF9800' : '#f5f5f5',
            color: activeTab === 'history' ? '#fff' : '#333',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: activeTab === 'history' ? 'bold' : 'normal',
            fontSize: '14px'
          }}
        >
          📜 Follow-up History
        </button>
      </div>

      {/* Member Follow-ups Tab */}
      {activeTab === 'members' && (
        <>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <button style={filterButtonStyle(actionFilter === 'all')} onClick={() => setActionFilter('all')}>
              All ({todoItems.length})
            </button>
            <button style={{ ...filterButtonStyle(actionFilter === 'overdue'), background: actionFilter === 'overdue' ? '#f44336' : '#ffebee', color: actionFilter === 'overdue' ? 'white' : '#c62828' }} onClick={() => setActionFilter('overdue')}>
              🚨 Overdue ({todoItems.filter(i => i.type === 'overdue').length})
            </button>
            <button style={{ ...filterButtonStyle(actionFilter === 'renewal'), background: actionFilter === 'renewal' ? '#FF9800' : '#fff3e0', color: actionFilter === 'renewal' ? 'white' : '#e65100' }} onClick={() => setActionFilter('renewal')}>
              🔄 Renewals ({todoItems.filter(i => i.type === 'renewal').length})
            </button>
            <button style={{ ...filterButtonStyle(actionFilter === 'trial'), background: actionFilter === 'trial' ? '#4CAF50' : '#e8f5e9', color: actionFilter === 'trial' ? 'white' : '#2e7d32' }} onClick={() => setActionFilter('trial')}>
              🆕 Trial ({todoItems.filter(i => i.type === 'trial').length})
            </button>
          </div>

          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '20px' }}>
            <div style={{ background: '#ffebee', padding: '15px', borderRadius: '10px', textAlign: 'center', border: '2px solid #f44336' }}>
              <h3 style={{ margin: 0, fontSize: '32px', color: '#c62828' }}>{todoItems.filter(i => i.type === 'overdue').length}</h3>
              <p style={{ margin: '5px 0 0', color: '#c62828', fontWeight: 'bold', fontSize: '13px' }}>Overdue</p>
            </div>
            <div style={{ background: '#fff3e0', padding: '15px', borderRadius: '10px', textAlign: 'center', border: '2px solid #FF9800' }}>
              <h3 style={{ margin: 0, fontSize: '32px', color: '#e65100' }}>{todoItems.filter(i => i.type === 'renewal').length}</h3>
              <p style={{ margin: '5px 0 0', color: '#e65100', fontWeight: 'bold', fontSize: '13px' }}>Renewals</p>
            </div>
            <div style={{ background: '#e8f5e9', padding: '15px', borderRadius: '10px', textAlign: 'center', border: '2px solid #4CAF50' }}>
              <h3 style={{ margin: 0, fontSize: '32px', color: '#2e7d32' }}>{todoItems.filter(i => i.type === 'trial').length}</h3>
              <p style={{ margin: '5px 0 0', color: '#2e7d32', fontWeight: 'bold', fontSize: '13px' }}>Trial</p>
            </div>
          </div>

          {/* Member Action Items List */}
          <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '20px', borderRadius: '10px' }}>
            {/* Bulk Action Bar */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
              padding: '10px 15px',
              background: 'rgba(255,255,255,0.95)',
              borderRadius: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  checked={selectedMembers.length === filteredTodoItems.length && filteredTodoItems.length > 0}
                  onChange={() => toggleAllMembers(filteredTodoItems)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                  {selectedMembers.length > 0 ? `${selectedMembers.length} selected` : 'Select all'}
                </span>
              </div>
              {selectedMembers.length > 0 && (
                <button
                  onClick={() => handleBulkWhatsApp(filteredTodoItems)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    background: '#25D366',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  <WhatsAppIcon size={18} />
                  Bulk WhatsApp ({selectedMembers.length})
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gap: '10px', maxHeight: '500px', overflowY: 'auto' }}>
              {filteredTodoItems.map((item, index) => (
                <div key={index} style={{
                  background: selectedMembers.includes(item.member.id) ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.95)',
                  color: '#333',
                  padding: '12px 15px',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderLeft: `4px solid ${item.priority === 'high' ? '#f44336' : '#FF9800'}`,
                  boxShadow: selectedMembers.includes(item.member.id) ? '0 0 0 2px #667eea' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(item.member.id)}
                      onChange={() => toggleMember(item.member.id)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '3px' }}>{item.message}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>📞 {item.member.phone} | Due: {formatDate(item.member.renewalDue)}</div>
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>💡 {item.action}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => handleLogFollowUp(item.member, 'member')}
                      style={{
                        padding: '4px 10px',
                        background: '#9C27B0',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                    >
                      📝 Log
                    </button>
                    <a
                      href={`https://wa.me/91${item.member.phone?.replace(/\D/g, '')}?text=${encodeURIComponent(generateMessage(messageSettings.whatsappTemplate, item.member, item.type === 'overdue' ? 'expired' : 'expiring soon'))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                    >
                      <WhatsAppIcon size={22} />
                    </a>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '15px',
                      fontSize: '11px',
                      background: item.priority === 'high' ? '#f44336' : '#FF9800',
                      color: 'white'
                    }}>
                      {item.priority.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
              {filteredTodoItems.length === 0 && (
                <div style={{ background: 'rgba(255,255,255,0.9)', color: '#333', padding: '30px', borderRadius: '8px', textAlign: 'center' }}>
                  ✅ No action items - All members are up to date!
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Lead Follow-ups Tab */}
      {activeTab === 'leads' && (
        <>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
            <button style={filterButtonStyle(leadFilter === 'all')} onClick={() => setLeadFilter('all')}>
              All ({leadItems.length})
            </button>
            <button style={{ ...filterButtonStyle(leadFilter === 'overdue'), background: leadFilter === 'overdue' ? '#f44336' : '#ffebee', color: leadFilter === 'overdue' ? 'white' : '#c62828' }} onClick={() => setLeadFilter('overdue')}>
              🚨 Overdue ({leadItems.filter(i => i.type === 'overdue').length})
            </button>
            <button style={{ ...filterButtonStyle(leadFilter === 'today'), background: leadFilter === 'today' ? '#FF9800' : '#fff3e0', color: leadFilter === 'today' ? 'white' : '#e65100' }} onClick={() => setLeadFilter('today')}>
              📅 Today ({leadItems.filter(i => i.type === 'today').length})
            </button>
            <button style={{ ...filterButtonStyle(leadFilter === 'upcoming'), background: leadFilter === 'upcoming' ? '#2196F3' : '#e3f2fd', color: leadFilter === 'upcoming' ? 'white' : '#1565c0' }} onClick={() => setLeadFilter('upcoming')}>
              ⏰ Upcoming ({leadItems.filter(i => i.type === 'upcoming').length})
            </button>
            <button style={{ ...filterButtonStyle(leadFilter === 'pending'), background: leadFilter === 'pending' ? '#9C27B0' : '#f3e5f5', color: leadFilter === 'pending' ? 'white' : '#6a1b9a' }} onClick={() => setLeadFilter('pending')}>
              📋 Pending ({leadItems.filter(i => i.type === 'pending').length})
            </button>
          </div>

          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '20px' }}>
            <div style={{ background: '#ffebee', padding: '15px', borderRadius: '10px', textAlign: 'center', border: '2px solid #f44336' }}>
              <h3 style={{ margin: 0, fontSize: '32px', color: '#c62828' }}>{leadItems.filter(i => i.type === 'overdue').length}</h3>
              <p style={{ margin: '5px 0 0', color: '#c62828', fontWeight: 'bold', fontSize: '13px' }}>Overdue</p>
            </div>
            <div style={{ background: '#fff3e0', padding: '15px', borderRadius: '10px', textAlign: 'center', border: '2px solid #FF9800' }}>
              <h3 style={{ margin: 0, fontSize: '32px', color: '#e65100' }}>{leadItems.filter(i => i.type === 'today').length}</h3>
              <p style={{ margin: '5px 0 0', color: '#e65100', fontWeight: 'bold', fontSize: '13px' }}>Today</p>
            </div>
            <div style={{ background: '#e3f2fd', padding: '15px', borderRadius: '10px', textAlign: 'center', border: '2px solid #2196F3' }}>
              <h3 style={{ margin: 0, fontSize: '32px', color: '#1565c0' }}>{leadItems.filter(i => i.type === 'upcoming').length}</h3>
              <p style={{ margin: '5px 0 0', color: '#1565c0', fontWeight: 'bold', fontSize: '13px' }}>Upcoming</p>
            </div>
            <div style={{ background: '#f3e5f5', padding: '15px', borderRadius: '10px', textAlign: 'center', border: '2px solid #9C27B0' }}>
              <h3 style={{ margin: 0, fontSize: '32px', color: '#6a1b9a' }}>{leadItems.filter(i => i.type === 'pending').length}</h3>
              <p style={{ margin: '5px 0 0', color: '#6a1b9a', fontWeight: 'bold', fontSize: '13px' }}>Pending</p>
            </div>
          </div>

          {/* Lead Items List */}
          <div style={{ background: 'linear-gradient(135deg, #9C27B0 0%, #673AB7 100%)', padding: '20px', borderRadius: '10px' }}>
            <div style={{ display: 'grid', gap: '10px', maxHeight: '500px', overflowY: 'auto' }}>
              {filteredLeadItems.map((item, index) => (
                <div key={index} style={{
                  background: 'rgba(255,255,255,0.95)',
                  color: '#333',
                  padding: '12px 15px',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderLeft: `4px solid ${item.type === 'overdue' ? '#f44336' : item.type === 'today' ? '#FF9800' : item.type === 'upcoming' ? '#2196F3' : '#9C27B0'}`
                }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '3px' }}>{item.message}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      📞 {item.lead.phone} | Source: {item.lead.source || '-'} | Status: {item.lead.status}
                    </div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>💡 {item.action}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => handleLogFollowUp(item.lead, 'lead')}
                      style={{
                        padding: '4px 10px',
                        background: '#9C27B0',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                    >
                      📝 Log
                    </button>
                    <a
                      href={`https://wa.me/91${item.lead.phone?.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${item.lead.name}, we wanted to follow up on your interest in joining our gym. Would you like to schedule a visit?`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                    >
                      <WhatsAppIcon size={22} />
                    </a>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '15px',
                      fontSize: '11px',
                      background: item.priority === 'high' ? '#f44336' : item.priority === 'medium' ? '#FF9800' : '#4CAF50',
                      color: 'white'
                    }}>
                      {item.priority.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
              {filteredLeadItems.length === 0 && (
                <div style={{ background: 'rgba(255,255,255,0.9)', color: '#333', padding: '30px', borderRadius: '8px', textAlign: 'center' }}>
                  ✅ No lead follow-ups pending!
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Follow-up History Tab */}
      {activeTab === 'history' && (
        <div style={{ display: 'grid', gap: '20px' }}>
          {/* Member Follow-up History */}
          <div style={{ background: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>👥 Member Follow-up History</h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {memberFollowUps.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Member</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Contact Mode</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Outcome</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Notes</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Followed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberFollowUps.slice(0, 20).map((f, idx) => {
                      const member = members.find(m => m.id === f.memberId);
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '10px' }}>{formatDate(f.contactDate || f.createdAt)}</td>
                          <td style={{ padding: '10px' }}>{member?.name || f.memberId}</td>
                          <td style={{ padding: '10px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              background: f.contactMode === 'WhatsApp' ? '#25D366' : f.contactMode === 'Call' ? '#2196F3' : f.contactMode === 'Email' ? '#FF9800' : '#9C27B0',
                              color: 'white'
                            }}>
                              {f.contactMode || '-'}
                            </span>
                          </td>
                          <td style={{ padding: '10px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              background: f.outcome === 'Renewed' ? '#4CAF50' : f.outcome === 'Promised' ? '#2196F3' : f.outcome === 'Declined' ? '#f44336' : '#9e9e9e',
                              color: 'white'
                            }}>
                              {f.outcome || '-'}
                            </span>
                          </td>
                          <td style={{ padding: '10px' }}>{f.notes || '-'}</td>
                          <td style={{ padding: '10px' }}>{f.followedUpBy || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: '30px', textAlign: 'center', color: '#888' }}>No follow-up history yet</div>
              )}
            </div>
          </div>

          {/* Lead Follow-up History */}
          <div style={{ background: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0 }}>🎯 Lead Follow-up History</h3>
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {leadFollowUps.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Lead</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Response</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>Next Follow-up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leadFollowUps.slice(0, 20).map((f, idx) => {
                      const lead = leads.find(l => l.id === f.leadId);
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '10px' }}>{formatDate(f.followUpDate)}</td>
                          <td style={{ padding: '10px' }}>{lead?.name || f.leadId}</td>
                          <td style={{ padding: '10px' }}>{f.response || '-'}</td>
                          <td style={{ padding: '10px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              background: f.statusAfter === 'Converted' ? '#4CAF50' : f.statusAfter === 'Not Interested' ? '#f44336' : '#2196F3',
                              color: 'white'
                            }}>
                              {f.statusAfter}
                            </span>
                          </td>
                          <td style={{ padding: '10px' }}>{formatDate(f.nextFollowUpDate)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: '30px', textAlign: 'center', color: '#888' }}>No lead follow-up history yet</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Modal */}
      {showFollowUpModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '10px', padding: '30px', width: '450px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>📝 Log Follow-up for {selectedItem?.name}</h3>
            <form onSubmit={handleSubmitFollowUp}>
              {itemType === 'member' ? (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Contact Mode *</label>
                    <select
                      value={followUpForm.contactMode}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, contactMode: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                      required
                    >
                      <option value="WhatsApp">📱 WhatsApp</option>
                      <option value="Call">📞 Call</option>
                      <option value="Email">📧 Email</option>
                      <option value="In-Person">🤝 In-Person</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Outcome *</label>
                    <select
                      value={followUpForm.outcome}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, outcome: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                      required
                    >
                      <option value="Promised">🤝 Promised to Renew</option>
                      <option value="Renewed">✅ Renewed</option>
                      <option value="No Response">📵 No Response</option>
                      <option value="Declined">❌ Declined</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Notes</label>
                    <textarea
                      value={followUpForm.notes}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, notes: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', minHeight: '80px' }}
                      placeholder="Enter follow-up notes..."
                    />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Response</label>
                    <textarea
                      value={followUpForm.response}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, response: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box', minHeight: '80px' }}
                      placeholder="What was the lead's response?"
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Status After</label>
                    <select
                      value={followUpForm.status}
                      onChange={(e) => setFollowUpForm({ ...followUpForm, status: e.target.value })}
                      style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                    >
                      <option value="">Keep Current</option>
                      <option value="Contacted">Contacted</option>
                      <option value="Interested">Interested</option>
                      <option value="Not Interested">Not Interested</option>
                      <option value="Converted">Converted</option>
                    </select>
                  </div>
                </>
              )}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Next Follow-up Date</label>
                <input
                  type="date"
                  value={followUpForm.nextFollowUpDate}
                  onChange={(e) => setFollowUpForm({ ...followUpForm, nextFollowUpDate: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowFollowUpModal(false)}
                  style={{ padding: '10px 20px', backgroundColor: '#ccc', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '10px 20px', backgroundColor: '#9C27B0', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                >
                  Log Follow-up
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUp;
